import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { useLanguage } from '../LanguageContext';
import './AccountPages.css';

const yuan = cents => `¥${((Number(cents) || 0) / 100).toFixed(2)}`;

const emptyDisputeForm = { reason: '', detail: '', evidence: '' };

function transactionStatusKey(transaction) {
  return transaction?.status || transaction?.type || '';
}

export default function Wallet() {
  const { language } = useLanguage();
  const isZh = language === 'zh';
  const text = isZh ? {
    title: '财务中心',
    checkout: '发起托管付款',
    balance: '可用余额',
    frozen: '托管中',
    transactions: '流水数量',
    recent: '最近交易',
    orders: '订单记录',
    disputes: '订单争议',
    checkoutRequests: '托管付款申请',
    disputeHistory: '争议记录',
    openDispute: '发起争议',
    cancelDispute: '取消',
    releaseOrder: '确认完成并放款',
    releasingOrder: '放款中...',
    releaseSuccess: '订单已确认完成，款项已结算到服务者余额',
    disputeReason: '争议原因',
    disputeDetail: '补充说明',
    disputeEvidence: '证据链接',
    submitDispute: '提交争议',
    disputeSuccess: '争议已提交，等待平台处理',
    emptyDisputes: '暂无订单争议。',
    withdraw: '提现申请',
    buyer: '我是雇主',
    seller: '我是服务者',
    loading: '加载中...',
    type: '类型',
    amount: '金额',
    description: '说明',
    status: '状态',
    transactionStatus: {
      recharge: '已完成',
      escrow_hold: '已托管',
      escrow_release: '已完成',
      refund: '已退款',
      withdraw_freeze: '处理中',
      withdraw_unfreeze: '已退回',
      withdraw_completed: '已完成',
      admin_adjust: '已完成',
    },
    time: '时间',
    action: '操作',
    order: '订单',
    task: '需求',
    party: '相关方',
    withdrawAmount: '提现金额（元）',
    withdrawBelowMinimum: amount => `提现金额不能低于 ¥${amount.toFixed(2)}`,
    name: '支付宝实名姓名',
    account: '支付宝账号',
    withdrawExceedsBalance: '提现金额不能超过可用余额',
    submit: '提交提现',
    success: '提现申请已提交，等待平台审核',
    failed: '提交失败',
    emptyTx: '暂无交易流水。',
    emptyCheckoutRequests: '暂无托管付款申请。通过支付宝完成托管付款后，付款状态会显示在这里。',
    emptyOrders: '暂无订单。完成报价选择或托管付款后，订单会显示在这里。',
    checkoutStatus: {
      pending: '待处理',
      verifying: '待核验付款',
      processing: '处理中',
      paid: '已支付',
      completed: '已完成',
      cancelled: '已取消',
    },
    orderStatus: {
      pending_payment: '待托管付款',
      escrow: '托管中',
      completed: '已完成',
      refunded: '已退款',
      cancelled: '已取消',
      in_progress: '进行中',
      awaiting_acceptance: '待验收',
      new_order: '新订单',
      delivered: '已交付',
      refund_pending: '退款中',
    },
  } : {
    title: 'Finance Center',
    checkout: 'Start escrow payment',
    balance: 'Available balance',
    frozen: 'In escrow',
    transactions: 'Transactions',
    recent: 'Recent transactions',
    orders: 'Orders',
    disputes: 'Order disputes',
    checkoutRequests: 'Escrow payment requests',
    disputeHistory: 'Dispute history',
    openDispute: 'Open dispute',
    cancelDispute: 'Cancel',
    releaseOrder: 'Confirm completion',
    releasingOrder: 'Releasing...',
    releaseSuccess: 'Order completed and funds released to the provider balance.',
    disputeReason: 'Reason',
    disputeDetail: 'Details',
    disputeEvidence: 'Evidence links',
    submitDispute: 'Submit dispute',
    disputeSuccess: 'Dispute submitted and awaiting platform review.',
    emptyDisputes: 'No order disputes yet.',
    withdraw: 'Withdrawal request',
    buyer: 'As client',
    seller: 'As provider',
    loading: 'Loading...',
    type: 'Type',
    amount: 'Amount',
    description: 'Description',
    status: 'Status',
    transactionStatus: {
      recharge: 'Completed',
      escrow_hold: 'Escrowed',
      escrow_release: 'Completed',
      refund: 'Refunded',
      withdraw_freeze: 'Processing',
      withdraw_unfreeze: 'Returned',
      withdraw_completed: 'Completed',
      admin_adjust: 'Completed',
    },
    time: 'Time',
    action: 'Action',
    order: 'Order',
    task: 'Task',
    party: 'Party',
    withdrawAmount: 'Withdrawal amount',
    withdrawBelowMinimum: amount => `Withdrawal amount cannot be lower than ¥${amount.toFixed(2)}`,
    name: 'Alipay real name',
    account: 'Alipay account',
    withdrawExceedsBalance: 'Withdrawal amount cannot exceed your available balance.',
    submit: 'Submit withdrawal',
    success: 'Withdrawal request submitted and awaiting platform review.',
    failed: 'Submission failed',
    emptyTx: 'No transactions yet.',
    emptyCheckoutRequests: 'No escrow payment requests yet. Alipay checkout records will appear here.',
    emptyOrders: 'No orders yet. Orders will appear after a quote is accepted or an escrow payment is created.',
    checkoutStatus: {
      pending: 'Pending',
      verifying: 'Verifying payment',
      processing: 'Processing',
      paid: 'Paid',
      completed: 'Completed',
      cancelled: 'Cancelled',
    },
    orderStatus: {
      pending_payment: 'Pending escrow payment',
      escrow: 'In escrow',
      completed: 'Completed',
      refunded: 'Refunded',
      cancelled: 'Cancelled',
      in_progress: 'In progress',
      awaiting_acceptance: 'Awaiting acceptance',
      new_order: 'New order',
      delivered: 'Delivered',
      refund_pending: 'Refund in review',
    },
  };

  const [wallet, setWallet] = useState({ balance: 0, frozen: 0, recent: [], minWithdrawalAmount: 50 });
  const [checkoutRequests, setCheckoutRequests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [role, setRole] = useState('buyer');
  const [form, setForm] = useState({ amount: '', name: '', account: '' });
  const [disputeForms, setDisputeForms] = useState({});
  const [activeDisputeOrderId, setActiveDisputeOrderId] = useState(null);
  const [message, setMessage] = useState('');
  const [disputeMessage, setDisputeMessage] = useState('');
  const [releasingOrderId, setReleasingOrderId] = useState(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [checkoutRequestsLoading, setCheckoutRequestsLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [disputesLoading, setDisputesLoading] = useState(true);

  const loadDisputes = useCallback(() => {
    setDisputesLoading(true);
    return api.get('/payments/disputes')
      .then(r => setDisputes(r.data.disputes || []))
      .finally(() => setDisputesLoading(false));
  }, []);

  const loadWallet = useCallback(() => {
    setWalletLoading(true);
    return api.get('/payments/wallet')
      .then(r => setWallet(r.data))
      .finally(() => setWalletLoading(false));
  }, []);

  const loadOrders = useCallback(() => {
    setOrdersLoading(true);
    return api.get('/payments/orders', { params: { role } })
      .then(r => setOrders(r.data.orders || []))
      .finally(() => setOrdersLoading(false));
  }, [role]);

  useEffect(() => {
    loadWallet();
  }, [loadWallet]);

  useEffect(() => {
    setCheckoutRequestsLoading(true);
    api.get('/payments/checkout-requests')
      .then(r => setCheckoutRequests(r.data.requests || []))
      .finally(() => setCheckoutRequestsLoading(false));
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    loadDisputes();
  }, [loadDisputes]);

  const submitWithdraw = async e => {
    e.preventDefault();
    setMessage('');
    try {
      const withdrawAmountCents = Math.round(Number(form.amount) * 100);
      if (withdrawAmountCents > Number(wallet.balance || 0)) {
        setMessage(text.withdrawExceedsBalance);
        return;
      }
      const minWithdrawalCents = Math.round(Number(wallet.minWithdrawalAmount || 0) * 100);
      if (minWithdrawalCents > 0 && withdrawAmountCents < minWithdrawalCents) {
        setMessage(text.withdrawBelowMinimum(Number(wallet.minWithdrawalAmount || 0)));
        return;
      }
      await api.post('/payments/withdrawals', {
        amount: withdrawAmountCents,
        account: {
          type: 'alipay',
          name: form.name.trim(),
          account: form.account.trim(),
        },
      });
      setForm({ amount: '', name: '', account: '' });
      setMessage(text.success);
    } catch (err) {
      setMessage(err.response?.data?.error || text.failed);
    }
  };

  const updateDisputeForm = (orderId, patch) => {
    setDisputeForms(forms => ({
      ...forms,
      [orderId]: { ...(forms[orderId] || emptyDisputeForm), ...patch },
    }));
  };

  const submitDispute = async (event, order) => {
    event.preventDefault();
    setDisputeMessage('');
    const current = disputeForms[order.id] || emptyDisputeForm;
    const evidence = current.evidence
      .split('\n')
      .map(item => item.trim())
      .filter(Boolean);
    try {
      await api.post(`/payments/orders/${order.id}/disputes`, {
        reason: current.reason,
        detail: current.detail,
        evidence,
      });
      setDisputeForms(forms => ({ ...forms, [order.id]: emptyDisputeForm }));
      setActiveDisputeOrderId(null);
      setDisputeMessage(text.disputeSuccess);
      await loadDisputes();
    } catch (err) {
      setDisputeMessage(err.response?.data?.error || text.failed);
    }
  };

  const releaseOrder = async (order) => {
    setDisputeMessage('');
    setReleasingOrderId(order.id);
    try {
      await api.post(`/payments/orders/${order.id}/release`);
      setActiveDisputeOrderId(null);
      setDisputeMessage(text.releaseSuccess);
      await Promise.all([loadWallet(), loadOrders()]);
    } catch (err) {
      setDisputeMessage(err.response?.data?.error || text.failed);
    } finally {
      setReleasingOrderId(null);
    }
  };

  const hasOpenDispute = orderId => disputes.some(d => Number(d.order_id) === Number(orderId) && d.status === 'open');
  const getOrderViewStatus = order => {
    const currentDispute = disputes.find(d => Number(d.order_id) === Number(order.id) && d.status === 'open');
    const isRefundRequest = /退款|refund/i.test(String(currentDispute?.reason || ''));
    if (isRefundRequest) return 'refund_pending';
    return role === 'seller' ? (order.seller_status || order.status) : (order.buyer_status || order.status);
  };

  return (
    <div className="account-page">
      <div className="container">
        <div className="account-head finance-head">
          <div>
            <h1>{text.title}</h1>
          </div>
          <Link className="btn btn-primary" to="/checkout">{text.checkout}</Link>
        </div>

        <div className="finance-unified">
          <section className="finance-block">
            <div className="account-grid finance-metrics">
              <div className="metric-card"><span>{text.balance}</span><strong>{yuan(wallet.balance)}</strong></div>
              <div className="metric-card"><span>{text.frozen}</span><strong>{yuan(wallet.frozen)}</strong></div>
              <div className="metric-card"><span>{text.transactions}</span><strong>{wallet.recent?.length || 0}</strong></div>
            </div>
          </section>

          <section className="account-card finance-block">
            <h2>{text.recent}</h2>
            {walletLoading ? <div className="loading">{text.loading}</div> : wallet.recent?.length ? (
              <table className="account-table">
                <thead><tr><th>{text.type}</th><th>{text.amount}</th><th>{text.description}</th><th>{text.status}</th><th>{text.time}</th></tr></thead>
                <tbody>{wallet.recent.map(tx => (
                  <tr key={tx.id}>
                    <td>{tx.type}</td>
                    <td>{yuan(tx.amount)}</td>
                    <td>{tx.description || '-'}</td>
                    <td><span className="status-pill">{text.transactionStatus[transactionStatusKey(tx)] || tx.status || tx.type || '-'}</span></td>
                    <td>{tx.created_at || tx.createdAt}</td>
                  </tr>
                ))}</tbody>
              </table>
            ) : <div className="empty-state">{text.emptyTx}</div>}
          </section>

          <section className="account-card finance-block">
            <h2>{text.checkoutRequests}</h2>
            {checkoutRequestsLoading ? <div className="loading">{text.loading}</div> : checkoutRequests.length ? (
              <table className="account-table">
                <thead><tr><th>{text.order}</th><th>{text.description}</th><th>{text.amount}</th><th>{text.status}</th><th>{text.type}</th><th>{text.time}</th></tr></thead>
                <tbody>{checkoutRequests.map(item => (
                  <tr key={item.id}>
                    <td>#{item.id}</td>
                    <td>{item.title || item.description || '-'}</td>
                    <td>{yuan(item.amount)}</td>
                    <td><span className="status-pill">{text.checkoutStatus[item.status] || item.status}</span></td>
                    <td>{[item.payment_method, item.payment_reference].filter(Boolean).join(' / ') || '-'}</td>
                    <td>{item.updated_at || item.proof_submitted_at || item.created_at || item.createdAt}</td>
                  </tr>
                ))}</tbody>
              </table>
            ) : <div className="empty-state">{text.emptyCheckoutRequests}</div>}
          </section>

          <section className="account-card finance-block">
            <div className="finance-card-head">
              <h2>{text.orders}</h2>
              <select className="market-select" value={role} onChange={e => setRole(e.target.value)}>
                <option value="buyer">{text.buyer}</option>
                <option value="seller">{text.seller}</option>
              </select>
            </div>
            {disputeMessage && <div className="account-note finance-inline-note">{disputeMessage}</div>}
            {ordersLoading ? <div className="loading">{text.loading}</div> : orders.length ? (
              <table className="account-table">
                <thead><tr><th>{text.order}</th><th>{text.amount}</th><th>{text.status}</th><th>{text.task}</th><th>{text.time}</th><th>{text.action}</th></tr></thead>
                <tbody>{orders.map(o => (
                  <React.Fragment key={o.id}>
                    <tr>
                          <td><Link className="finance-order-link" to={`/orders/${o.id}`}>#{o.order_no || o.id}</Link></td>
                      <td>{yuan(o.amount)}</td>
                      <td><span className="status-pill">{text.orderStatus[getOrderViewStatus(o)] || getOrderViewStatus(o)}</span></td>
                      <td>{o.task_title || o.task_id || '-'}</td>
                      <td>{o.created_at || o.createdAt}</td>
                      <td>
                        <div className="finance-order-actions">
                          {role === 'buyer' && o.status === 'escrow' && !hasOpenDispute(o.id) && (
                            <button
                              className="btn btn-sm btn-primary"
                              type="button"
                              disabled={releasingOrderId === o.id}
                              onClick={() => releaseOrder(o)}
                            >
                              {releasingOrderId === o.id ? text.releasingOrder : text.releaseOrder}
                            </button>
                          )}
                          {o.status === 'escrow' && !hasOpenDispute(o.id) && (
                            <button
                              className="btn btn-sm btn-outline"
                              type="button"
                              onClick={() => setActiveDisputeOrderId(activeDisputeOrderId === o.id ? null : o.id)}
                            >
                              {activeDisputeOrderId === o.id ? text.cancelDispute : text.openDispute}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {activeDisputeOrderId === o.id && (
                      <tr className="finance-dispute-row">
                        <td colSpan={6}>
                          <form className="account-form finance-dispute-form" onSubmit={event => submitDispute(event, o)}>
                            <label>
                              {text.disputeReason}
                              <input
                                required
                                maxLength={120}
                                value={(disputeForms[o.id] || emptyDisputeForm).reason}
                                onChange={event => updateDisputeForm(o.id, { reason: event.target.value })}
                              />
                            </label>
                            <label>
                              {text.disputeDetail}
                              <textarea
                                rows={3}
                                maxLength={2000}
                                value={(disputeForms[o.id] || emptyDisputeForm).detail}
                                onChange={event => updateDisputeForm(o.id, { detail: event.target.value })}
                              />
                            </label>
                            <label>
                              {text.disputeEvidence}
                              <textarea
                                rows={2}
                                value={(disputeForms[o.id] || emptyDisputeForm).evidence}
                                onChange={event => updateDisputeForm(o.id, { evidence: event.target.value })}
                              />
                            </label>
                            <button className="btn btn-primary" type="submit">{text.submitDispute}</button>
                          </form>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}</tbody>
              </table>
            ) : <div className="empty-state">{text.emptyOrders}</div>}
          </section>

          <section className="account-card finance-block">
            <h2>{text.disputeHistory}</h2>
            {disputesLoading ? <div className="loading">{text.loading}</div> : disputes.length ? (
              <table className="account-table">
                <thead><tr><th>{text.order}</th><th>{text.status}</th><th>{text.description}</th><th>{text.party}</th><th>{text.time}</th></tr></thead>
                <tbody>{disputes.map(d => (
                  <tr key={d.id}>
                    <td>#{d.order_id}</td>
                    <td><span className="status-pill">{d.status}</span></td>
                    <td>{d.reason || '-'}</td>
                    <td>{d.opened_by === d.against_user_id ? '-' : (d.buyer_name || d.seller_name || '-')}</td>
                    <td>{d.created_at || d.createdAt}</td>
                  </tr>
                ))}</tbody>
              </table>
            ) : <div className="empty-state">{text.emptyDisputes}</div>}
          </section>

          <section className="account-card finance-block">
            <h2>{text.withdraw}</h2>
            <form className="account-form finance-withdraw-form" onSubmit={submitWithdraw}>
              <label>{text.withdrawAmount}<input required type="number" inputMode="decimal" step="0.01" min={Math.max(1, Number(wallet.minWithdrawalAmount || 0))} max={Math.max(0, Number(wallet.balance || 0) / 100)} value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} /></label>
              <label>{text.name}<input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></label>
              <label>{text.account}<input required value={form.account} onChange={e => setForm(f => ({ ...f, account: e.target.value }))} /></label>
              <button className="btn btn-primary" type="submit">{text.submit}</button>
              {message && <div className="account-note">{message}</div>}
            </form>
          </section>
        </div>
      </div>
    </div>
  );
}
